// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { SYNC_ITEM_STATUSES, SYNC_JOB_TYPES, SYNC_STATUSES, SYNC_TRIGGERS } from './enums.js';
import { isoDateTimeSchema, paginationQuerySchema, uuidSchema } from './common.js';
export const syncAttendanceRequestSchema = z.object({
    deviceIds: z.array(uuidSchema).max(1000).optional(),
    branchId: uuidSchema.optional(),
    groupId: uuidSchema.optional(),
    all: z.boolean().default(false),
    /** Force a full re-pull from the provider's earliest cursor (reconciliation). */
    fullResync: z.boolean().default(false),
}).refine((v) => v.all || v.branchId || v.groupId || (v.deviceIds && v.deviceIds.length > 0), { message: 'Specify devices, a branch, a group, or all' });
export const syncEmployeesRequestSchema = z.object({
    employeeIds: z.array(uuidSchema).max(5000).optional(),
    branchId: uuidSchema.optional(),
    deviceIds: z.array(uuidSchema).max(1000).optional(),
    all: z.boolean().default(false),
    /** Also remove employees that should no longer exist on the device. */
    removeStale: z.boolean().default(false),
}).refine((v) => v.all || v.branchId || (v.employeeIds && v.employeeIds.length > 0) || (v.deviceIds && v.deviceIds.length > 0), { message: 'Specify employees, devices, a branch, or all' });
export const syncJobDtoSchema = z.object({
    id: uuidSchema,
    jobType: z.enum(SYNC_JOB_TYPES),
    trigger: z.enum(SYNC_TRIGGERS),
    scope: z.record(z.string(), z.unknown()),
    status: z.enum(SYNC_STATUSES),
    priority: z.number().int(),
    itemsTotal: z.number().int(),
    itemsSuccess: z.number().int(),
    itemsFailed: z.number().int(),
    itemsPending: z.number().int(),
    itemsOffline: z.number().int(),
    itemsUnsupported: z.number().int(),
    recordsIngested: z.number().int(),
    requestedBy: uuidSchema.nullable(),
    requestedByName: z.string().nullable().optional(),
    correlationId: z.string(),
    errorCode: z.string().nullable(),
    error: z.string().nullable(),
    summary: z.record(z.string(), z.unknown()).nullable(),
    queuedAt: isoDateTimeSchema.nullable(),
    startedAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const syncJobItemDtoSchema = z.object({
    id: uuidSchema,
    syncJobId: uuidSchema,
    deviceId: uuidSchema.nullable(),
    deviceName: z.string().nullable().optional(),
    deviceCode: z.string().nullable().optional(),
    employeeId: uuidSchema.nullable(),
    employeeNumber: z.string().nullable().optional(),
    employeeName: z.string().nullable().optional(),
    operation: z.enum(SYNC_JOB_TYPES),
    status: z.enum(SYNC_ITEM_STATUSES),
    attempts: z.number().int(),
    maxAttempts: z.number().int(),
    nextAttemptAt: isoDateTimeSchema.nullable(),
    lastErrorCode: z.string().nullable(),
    lastError: z.string().nullable(),
    result: z.record(z.string(), z.unknown()).nullable(),
    recordsIngested: z.number().int(),
    startedAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
});
export const syncJobListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(SYNC_STATUSES).optional(),
    jobType: z.enum(SYNC_JOB_TYPES).optional(),
    deviceId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
});
/** Realtime broadcast payloads (channel org:<id>:sync). */
export const syncProgressEventSchema = z.object({
    type: z.literal('sync.progress'),
    jobId: uuidSchema,
    status: z.enum(SYNC_STATUSES),
    itemsTotal: z.number().int(),
    itemsSuccess: z.number().int(),
    itemsFailed: z.number().int(),
    itemsPending: z.number().int(),
    itemsOffline: z.number().int(),
    itemsUnsupported: z.number().int(),
    recordsIngested: z.number().int(),
    at: isoDateTimeSchema,
});
export const deviceStatusEventSchema = z.object({
    type: z.literal('device.status'),
    deviceId: uuidSchema,
    connectionStatus: z.string(),
    lastHeartbeatAt: isoDateTimeSchema.nullable(),
    at: isoDateTimeSchema,
});
export const realtimeEventSchema = z.discriminatedUnion('type', [syncProgressEventSchema, deviceStatusEventSchema]);
/** Domain event names emitted to the outbox (§83). */
export const DOMAIN_EVENT_TYPES = [
    'employee.created', 'employee.updated', 'employee.deleted', 'employee.imported',
    'device.created', 'device.updated', 'device.online', 'device.offline', 'device.credentials_changed',
    'sync.queued', 'sync.completed', 'sync.failed', 'sync.item_failed',
    'attendance.created', 'attendance.updated', 'attendance.correction_submitted', 'attendance.correction_approved', 'attendance.correction_rejected',
    // day-close sweep (HR portal Prompt 3): one event per (employee, sweep run) listing the days marked UNEXCUSED; targeted via payload.userIds
    'attendance.unexcused_marked',
    'approval.pending', 'approval.reminder', 'approval.escalated', 'approval.decided', 'approval.info_requested', 'approval.info_answered', 'approval.reassigned', 'approval.bypassed',
    'report.ready', 'report.failed', 'subscription.limit_reached',
    'leave.requested', 'leave.approved', 'leave.rejected',
    // Flowza Finance connector: emitted by the worker after 3 consecutive pull/push failures of a connector device.
    'sync.finance.failed',
    // report sharing / schedules (HR portal Prompt 6a): one event per delivered recipient report; targeted via payload.userIds
    'report.scheduled_delivery',
    // employee portal attendance (HR portal Prompt 4): targeted via payload.userIds
    'attendance.note_submitted', 'attendance.note_decided', 'attendance.note_info_requested', 'attendance.selfie_submitted', 'attendance.selfie_decided',
    'attendance.punch_flagged', 'attendance.regularisation_decided', 'shift.swap_requested', 'shift.swap_decided',
    // Leave v2 (HR portal Prompt 7): a comment on a leave request (targeted via payload.userIds: the other side of the
    // thread), a year close carried balances forward (HR), comp-off credits expired (the employee, payload.userId)
    'leave.comment_added', 'leave.year_closed', 'leave.comp_off_expired',
    // Notifications & reminders (HR portal Prompt 8): an approver's question on a leave request, told to the employee
    // (targeted via payload.userIds), and the missing check-out reminder (worker task attendance.missing-punch-reminder,
    // one per employee-day, targeted at the employee's own login)
    'leave.info_requested', 'punch.missing_out',
    // Attendance alerts (customer requirements): a late arrival / early departure of today or yesterday, an absence and the
    // reminder the day before its excuse deadline, a missing check-in after the shift start, and the weekly thank-you message —
    // all targeted via payload.userIds (the employee's own login, plus the direct manager where the settings say so)
    'attendance.late_arrival', 'attendance.early_departure', 'attendance.absence_recorded', 'attendance.absence_reminder', 'punch.missing_in', 'attendance.appreciation',
    // Attendance watchlist (workforce controls §6): the monthly summary to each line manager (their flagged direct reports) and
    // to the observation.manage holders (their scope), and an observation placed / closed (the employee per
    // watchlist.notifyEmployee, the line managers other than the person who acted) — all targeted via payload.userIds
    'watchlist.monthly_summary', 'observation.placed', 'observation.closed',
    // Meetings (workforce controls §7): invitations, changes (rescheduled / cancelled / removed), the reminder before the start,
    // the no-show notices after the grace (attendee / organiser / line manager) and the automatic close's summary — targeted
    // via payload.userIds (the API and the worker's meetings sweep resolve the logins)
    'meeting.invited', 'meeting.updated', 'meeting.reminder', 'meeting.no_show', 'meeting.completed',
];
//# sourceMappingURL=sync.js.map